import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const teamMembers = [
  {
    name: 'Carlos Mendez',
    role: 'Lead Restoration Technician',
    cert: 'IICRC Certified · 12 Years Experience',
    img: 'https://public.readdy.ai/ai/img_res/1b8766b3329e09da94ea576ee7109f58.jpg',
  },
  {
    name: 'Lisa Tran',
    role: 'Project Manager',
    cert: 'IICRC Certified · 9 Years Experience',
    img: 'https://readdy.ai/api/search-image?query=professional%20female%20project%20manager%20smiling%20confident%20headshot%20clean%20white%20background%20studio%20lighting%20realistic%20photo&width=300&height=300&seq=team-lisa&orientation=squarish',
  },
  {
    name: 'James Okafor',
    role: 'Mold Remediation Specialist',
    cert: 'IICRC Certified · 7 Years Experience',
    img: 'https://readdy.ai/api/search-image?query=professional%20male%20mold%20remediation%20specialist%20smiling%20confident%20headshot%20clean%20white%20background%20studio%20lighting%20realistic%20photo&width=300&height=300&seq=team-james&orientation=squarish',
  },
  {
    name: 'Maria Gonzalez',
    role: 'Insurance Liaison',
    cert: 'Claims Specialist · 10 Years Experience',
    img: 'https://readdy.ai/api/search-image?query=professional%20female%20insurance%20liaison%20smiling%20confident%20headshot%20clean%20white%20background%20studio%20lighting%20realistic%20photo&width=300&height=300&seq=team-maria&orientation=squarish',
  },
];

const values = [
  { icon: 'ri-shield-check-line', title: 'Integrity', desc: 'We are honest and transparent in everything we do — from assessments to billing.' },
  { icon: 'ri-time-line', title: 'Urgency', desc: 'Water damage doesn\'t wait, and neither do we. We respond fast, every time.' },
  { icon: 'ri-award-line', title: 'Excellence', desc: 'We hold ourselves to the highest standards of workmanship and professionalism.' },
  { icon: 'ri-heart-line', title: 'Compassion', desc: 'We treat every property like it\'s our own and every customer like family.' },
];

const milestones = [
  { year: '2009', event: 'Indoor-Restore Services founded in Merced, CA' },
  { year: '2012', event: 'Expanded to serve all of Merced County' },
  { year: '2015', event: 'Achieved IICRC Master Certification for entire team' },
  { year: '2018', event: 'Launched commercial restoration division' },
  { year: '2021', event: 'Surpassed 500 successful restoration projects' },
  { year: '2024', event: 'Expanded services across the full Central Valley' },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mercedwaterrestoration.com';

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "mainEntity": {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/#organization`,
        "name": "Indoor-Restore Services",
        "description": "Professional water damage restoration, fire damage cleanup, mold remediation, and emergency restoration services in Merced, CA. IICRC certified with 24/7 emergency response.",
        "url": siteUrl,
        "telephone": "+12094512288",
        "email": "info@mercedwaterrestoration.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1729 U St",
          "addressLocality": "Merced",
          "addressRegion": "CA",
          "postalCode": "95340",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "37.3022",
          "longitude": "-120.4829"
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Merced",
            "containedInPlace": {
              "@type": "State",
              "name": "California"
            }
          }
        ],
        "priceRange": "$$",
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "18:00"
          }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Restoration Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Water Damage Restoration",
                "description": "24/7 emergency water extraction and structural drying services"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Fire Damage Restoration",
                "description": "Complete fire and smoke damage cleanup and reconstruction"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Mold Remediation",
                "description": "EPA-compliant mold inspection, removal, and prevention"
              }
            }
          ]
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 overflow-hidden">
        <div className="relative h-80 md:h-96">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20water%20damage%20restoration%20team%20standing%20together%20in%20front%20of%20service%20van%20wearing%20uniforms%20Merced%20California%20sunny%20day%20realistic%20photo%20wide%20shot&width=1440&height=400&seq=about-hero-banner&orientation=landscape"
            alt="Indoor-Restore Services team in Merced CA"
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">About Indoor-Restore Services</h1>
            <p className="text-lg text-white/90 max-w-2xl">
              Merced's trusted water damage restoration experts — serving the Central Valley with integrity since 2009.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-teal-600 text-sm font-semibold uppercase tracking-widest">Our Story</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-6">
                Trusted Water Damage Experts in Merced
              </h2>
              <p className="text-lg text-gray-600 mb-5 leading-relaxed">
                Indoor-Restore Services is your trusted partner for emergency water damage restoration in Merced, California. With over 15 years of experience and a commitment to excellence, we provide fast, reliable, and professional restoration services to residential and commercial properties throughout the Central Valley.
              </p>
              <p className="text-lg text-gray-600 mb-5 leading-relaxed">
                Our team of certified technicians uses state-of-the-art equipment and proven techniques to restore your property quickly and efficiently. We understand the stress and disruption that water damage can cause, which is why we're available 24/7 to respond to emergencies.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                From initial assessment to final restoration, we work directly with your insurance company to make the process as smooth as possible. Our goal is to return your property to its pre-damage condition while minimizing disruption to your life or business.
              </p>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-award-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Certified Professionals</h4>
                    <p className="text-sm text-gray-600">IICRC certified technicians</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-customer-service-2-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Insurance Assistance</h4>
                    <p className="text-sm text-gray-600">We work with all major insurers</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ height: '480px' }}>
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20water%20restoration%20technicians%20in%20uniform%20inspecting%20water%20damaged%20room%20with%20moisture%20meters%20and%20equipment%20clean%20bright%20interior%20Merced%20California%20realistic%20photo%20style&width=700&height=480&seq=about-indoor-restore&orientation=landscape"
                  alt="Indoor-Restore Services technicians at work in Merced CA"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white p-8 rounded-xl shadow-xl max-w-xs">
                <div className="text-5xl font-bold mb-2">15+</div>
                <div className="text-lg">Years of Experience in Water Damage Restoration</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '500+', label: 'Properties Restored' },
              { value: '15+', label: 'Years in Business' },
              { value: '24/7', label: 'Emergency Response' },
              { value: '98%', label: 'Customer Satisfaction' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-white/80 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-teal-600 text-sm font-semibold uppercase tracking-widest">What Drives Us</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-5">
                  <i className={`${v.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{v.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Indoor-Restore Services?</h2>
            <p className="text-lg text-white/90 max-w-3xl mx-auto">
              When water damage strikes, you need a restoration company you can trust. Here's why property owners throughout Merced choose us.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: 'ri-time-line', title: '24/7 Emergency Response', desc: 'We are available around the clock to respond to your water damage emergency. Our team arrives quickly to minimize damage and begin restoration immediately.' },
              { icon: 'ri-shield-check-line', title: 'Licensed & Insured', desc: 'Fully licensed, bonded, and insured for your protection. Our technicians are IICRC certified and trained in the latest restoration techniques.' },
              { icon: 'ri-tools-line', title: 'Advanced Equipment', desc: 'We use state-of-the-art water extraction, drying, and dehumidification equipment to ensure thorough and efficient restoration.' },
              { icon: 'ri-money-dollar-circle-line', title: 'Insurance Assistance', desc: 'We work directly with your insurance company to streamline the claims process and ensure you receive the coverage you deserve.' },
              { icon: 'ri-team-line', title: 'Experienced Team', desc: 'Our skilled technicians have years of experience handling all types of water damage, from minor leaks to major floods.' },
              { icon: 'ri-customer-service-2-line', title: 'Customer Satisfaction', desc: 'Your satisfaction is our priority. We provide transparent communication and quality workmanship on every project.' },
            ].map((r) => (
              <div key={r.title} className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center mb-6">
                  <i className={`${r.icon} text-3xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{r.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-teal-600 text-sm font-semibold uppercase tracking-widest">The People Behind the Work</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2">Meet Our Team</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <div key={member.name} className="text-center group">
                <div className="w-40 h-40 mx-auto rounded-full overflow-hidden mb-5 shadow-lg border-4 border-teal-100 group-hover:border-teal-400 transition-all duration-300">
                  <img src={member.img} alt={member.name} className="w-full h-full object-cover object-top" loading="lazy" decoding="async" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
                <p className="text-teal-600 text-sm font-medium mt-1">{member.role}</p>
                <p className="text-gray-500 text-xs mt-1">{member.cert}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-teal-600 text-sm font-semibold uppercase tracking-widest">Our Journey</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2">Company Milestones</h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-teal-200"></div>
            <div className="space-y-10">
              {milestones.map((m, i) => (
                <div key={m.year} className={`flex items-center gap-8 ${i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`flex-1 ${i % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 inline-block max-w-xs">
                      <p className="text-sm text-gray-700 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                  <div className="w-14 h-14 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 z-10 shadow-lg">
                    <span className="text-white text-xs font-bold">{m.year}</span>
                  </div>
                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Work With Us?</h2>
          <p className="text-gray-600 mb-8">
            Whether you're dealing with an emergency or planning ahead, our team is ready to help. Call us anytime — we're available 24/7.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:2094512288"
              className="bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call (209) 451-2288
            </a>
            <a
              href="/#contact"
              className="border-2 border-teal-600 text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Send Us a Message
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
